/**
 * TourMenu — guided-tour selection card.
 *
 * Plan 05-03 Task 2 (NAV-05). Top-left fixed-position card that lists every
 * tour in manifest.tours[]. Visible only when navState === 'freeWalk' so the
 * menu cannot be invoked during the streetcar ride or mid-tour.
 *
 * Interaction: clicking a tour button dispatches
 *   {type:'SELECT_TOUR', tourId: tour.id}
 * via useNavSend. The navMachine transitions freeWalk → guidedTour; NavRig
 * swaps WalkRig for TourRig; TourRig reads useTourStore.activeTourId — which
 * this component ALSO writes via useTourStore.setActiveTour — and begins
 * riding the tour's CatmullRomCurve3 spline.
 *
 * Why both setActiveTour AND send({type:'SELECT_TOUR', tourId}):
 *   - The machine event carries the tourId so the XState transition is
 *     self-describing and auditable (transitionLogger emits the event payload).
 *   - The store write is what TourRig actually consumes (the machine doesn't
 *     maintain per-tour context beyond the state name).
 *   - Separating concerns follows the same pattern as BoardingOverlay's
 *     {type:'START'} dispatch — the machine never owns pure data slices.
 *
 * Chrome: AESTHETIC-BRIEF Rule 7 — Goudy Old Style serif stack, cream #FDF6EE
 * background, espresso #3B2F2F text, 1px warm-ochre #D4A87E border. Mirrors
 * BoardingOverlay / EraToggle / SourcesPanel styling.
 *
 * Position: fixed top-left at z-index 45 — above EraToggle (40) so it never
 * sits behind the era-toggle button in overlap cases; below SourcesPanel (50)
 * which lives right-side.
 */
import { useNavStore } from '../store/useNavStore';
import { useNavSend } from '../navigation/NavContext';
import { useManifest } from '../content/useManifest';
import { useTourStore } from '../store/useTourStore';
import type { Tour } from '../content/schema';

const SERIF_STACK =
  '"Goudy Old Style", "Cormorant Garamond", Georgia, serif';
const CREAM = '#FDF6EE';
const ESPRESSO = '#3B2F2F';
const WARM_OCHRE = '#D4A87E';

export function TourMenu() {
  const navState = useNavStore((s) => s.navState);
  const { manifest } = useManifest();
  const send = useNavSend();
  const setActiveTour = useTourStore((s) => s.setActiveTour);

  // Only visible in freeWalk — the menu is the entry point INTO a guided tour.
  if (navState !== 'freeWalk') return null;

  const tours: Tour[] = manifest?.tours ?? [];
  if (tours.length === 0) return null;

  const handleSelect = (tour: Tour) => {
    // Write to the tour store so TourRig can find the tour object on its next
    // render. Then dispatch the machine event so the machine transitions to
    // `guidedTour` and NavRig mounts <TourRig />.
    setActiveTour(tour.id);
    send({ type: 'SELECT_TOUR', tourId: tour.id });
  };

  return (
    <section
      role="region"
      aria-label="Guided Tours"
      style={{
        position: 'fixed',
        top: 32,
        left: 32,
        zIndex: 45,
        background: CREAM,
        color: ESPRESSO,
        border: `1px solid ${WARM_OCHRE}`,
        fontFamily: SERIF_STACK,
        padding: '18px 22px',
        maxWidth: 320,
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      }}
    >
      <h2
        style={{
          fontFamily: SERIF_STACK,
          fontWeight: 400,
          fontSize: '1.1rem',
          letterSpacing: '0.04em',
          margin: '0 0 12px',
          borderBottom: `1px solid ${WARM_OCHRE}`,
          paddingBottom: 8,
        }}
      >
        Guided Tours
      </h2>
      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {tours.map((tour) => (
          <li key={tour.id}>
            <button
              type="button"
              onClick={() => handleSelect(tour)}
              aria-label={tour.name}
              style={{
                fontFamily: SERIF_STACK,
                fontSize: '0.95rem',
                width: '100%',
                textAlign: 'left',
                padding: '8px 12px',
                background: CREAM,
                color: ESPRESSO,
                border: `1px solid ${ESPRESSO}`,
                cursor: 'pointer',
                letterSpacing: '0.02em',
                lineHeight: 1.3,
              }}
            >
              {tour.name}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
